import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import type { CreateProductRequestDto, ProductRequestDto } from "../../../../api";
import type { ImportError } from "./my-product-bulk-upload-page";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { api, updateProductRequest } from "../../../api_client";
import { BulkImportPreview, type PreviewProduct } from "../components/bulk-import-preview";

const PREVIEW_KEY = "bulk-upload-results";
const CONFIRMED_KEY = "bulk-upload-confirmed";

interface StoredPreviews {
  previews: CreateProductRequestDto[];
  errors: ImportError[];
}

interface StoredConfirmed {
  requests: ProductRequestDto[];
  submittedIds: string[];
  errors: ImportError[];
}

export function saveBulkUploadResults(results: StoredPreviews) {
  sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(results));
  sessionStorage.removeItem(CONFIRMED_KEY);
}

export function clearBulkUploadResults() {
  sessionStorage.removeItem(PREVIEW_KEY);
  sessionStorage.removeItem(CONFIRMED_KEY);
}

export function MyProductBulkUploadResultsPage() {
  const navigate = useNavigate();
  const [previews, setPreviews] = useState<CreateProductRequestDto[]>([]);
  const [confirmed, setConfirmed] = useState<ProductRequestDto[]>([]);
  const [submittedIds, setSubmittedIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  useEffect(() => {
    let hasData = false;

    const confirmedRaw = sessionStorage.getItem(CONFIRMED_KEY);
    if (confirmedRaw) {
      try {
        const parsed: StoredConfirmed = JSON.parse(confirmedRaw);
        setConfirmed(parsed.requests ?? []);
        setSubmittedIds(new Set(parsed.submittedIds ?? []));
        setErrors(parsed.errors ?? []);
        setHasConfirmed(true);
        hasData = true;
      } catch { /* ignore */ }
    }

    const raw = sessionStorage.getItem(PREVIEW_KEY);
    if (raw) {
      try {
        const parsed: StoredPreviews = JSON.parse(raw);
        setPreviews(parsed.previews ?? []);
        if (!hasData) setErrors(parsed.errors ?? []);
        hasData = true;
      } catch { /* ignore */ }
    }

    if (!hasData) {
      navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD, { replace: true });
    }
  }, [navigate]);

  const persistConfirmedState = useCallback(
    (requests: ProductRequestDto[], submitted: Set<string>, errs: ImportError[]) => {
      sessionStorage.setItem(
        CONFIRMED_KEY,
        JSON.stringify({ requests, submittedIds: [...submitted], errors: errs }),
      );
    },
    [],
  );

  async function handleConfirmImport() {
    if (previews.length === 0) return;
    setIsConfirming(true);
    try {
      const res = await api.post<{ data: ProductRequestDto[]; errors: { product: string; message: string }[] }>(
        "/api/v1/product-requests/confirm-import",
        { dtos: previews },
      );
      const { data: created, errors: confirmErrors } = res.data;

      const allErrors = [
        ...errors,
        ...(confirmErrors ?? []).map((e) => ({ product: e.product, message: e.message })),
      ];

      setConfirmed(created ?? []);
      setPreviews([]);
      setHasConfirmed(true);
      setErrors(allErrors);

      sessionStorage.removeItem(PREVIEW_KEY);
      persistConfirmedState(created ?? [], new Set(), allErrors);

      if (created?.length) {
        showSuccessToast(`${created.length} product${created.length !== 1 ? "s" : ""} saved as drafts.`);
      } else {
        showErrorToast("Failed to create drafts.");
      }
    } catch {
      showErrorToast("Failed to confirm import. Please try again.");
    } finally {
      setIsConfirming(false);
    }
  }

  async function handleSubmitOne(product: PreviewProduct) {
    if (!product.id) return;
    setSubmittingId(product.id);
    try {
      await updateProductRequest(product.id, { isDraft: false } as any);
      const next = new Set([...submittedIds, product.id]);
      setSubmittedIds(next);
      persistConfirmedState(confirmed, next, errors);
      showSuccessToast(`"${product.name}" submitted for review.`);
    } catch {
      showErrorToast(`Failed to submit "${product.name}".`);
    } finally {
      setSubmittingId(null);
    }
  }

  async function handleSubmitAll() {
    const remaining = confirmed.filter((r) => !submittedIds.has(r.id!));
    if (remaining.length === 0) return;
    setIsSubmittingAll(true);
    let submitted = 0;
    let failed = 0;
    const next = new Set(submittedIds);
    for (const req of remaining) {
      try {
        await updateProductRequest(req.id!, { isDraft: false } as any);
        next.add(req.id!);
        submitted++;
      } catch {
        failed++;
      }
    }
    setSubmittedIds(next);
    persistConfirmedState(confirmed, next, errors);
    if (submitted > 0 && failed === 0) {
      showSuccessToast(`${submitted} product${submitted !== 1 ? "s" : ""} submitted for review.`);
    } else if (submitted > 0) {
      showSuccessToast(`${submitted} submitted, ${failed} failed.`);
    } else {
      showErrorToast("Failed to submit products for review.");
    }
    setIsSubmittingAll(false);
  }

  function handleProductClick(product: PreviewProduct & { _previewIndex?: number }) {
    if (product.id && product.status === "submitted") {
      navigate(
        AppRoutes.PRODUCT_REQUEST_DETAILS.replace(":id", product.id),
        { state: { fromBulkUpload: true } },
      );
    } else if (product.id) {
      navigate(
        AppRoutes.MY_PRODUCT_REQUEST_EDIT.replace(":id", product.id),
        { state: { fromBulkUpload: true } },
      );
    } else if (product._previewIndex != null) {
      navigate(
        AppRoutes.MY_PRODUCT_BULK_UPLOAD_PREVIEW.replace(":index", String(product._previewIndex)),
        { state: { bulkPreviewDto: previews[product._previewIndex], bulkPreviewIndex: product._previewIndex } },
      );
    }
  }

  const confirmedProducts: (PreviewProduct & { _previewIndex?: number })[] = confirmed.map((req) => ({
    id: req.id,
    name: req.name ?? "Untitled",
    image: req.image,
    status: submittedIds.has(req.id!) ? "submitted" as const : "draft" as const,
    variants: req.variants?.map((v) => ({
      sku: v.sku,
      image: v.image,
      pricingTiers: v.pricingTiers,
    })),
  }));

  const remainingPreviews: (PreviewProduct & { _previewIndex?: number })[] = previews.map((dto, idx) => ({
    _previewIndex: idx,
    name: dto.name,
    image: (dto as any).image,
    variants: dto.variants?.map((v) => ({
      sku: v.sku,
      image: v.image,
      pricingTiers: v.pricingTiers,
    })),
  }));

  const previewProducts = [...confirmedProducts, ...remainingPreviews];

  const previewCount = previews.length;
  const draftCount = confirmed.filter((r) => !submittedIds.has(r.id!)).length;

  return (
    <div className="flex min-h-full w-full flex-col bg-bgLight">
      <div className="flex flex-1 flex-col gap-6 px-8 pt-8 pb-6">

        <div className="flex items-center gap-2">
          <Button
            variant="ghostAccent"
            size="sm"
            leftIcon={<IconArrowLeft size={18} />}
            onClick={() => navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD)}
            className="!h-auto !px-0 !py-0"
          >
            <Heading variant="h3" tone="inherit">Bulk Upload</Heading>
          </Button>
          <Text variant="bodySm" tone="muted">/</Text>
          <Heading variant="h3" tone="dark">
            {confirmed.length > 0 ? "Import Results" : "Preview"}
          </Heading>
        </div>

        <BulkImportPreview
          requests={previewProducts}
          errors={errors}
          mode={confirmed.length > 0 ? "confirmed" : "preview"}
          onProductClick={handleProductClick}
          onProductSubmit={handleSubmitOne}
          submittingId={submittingId}
        />
      </div>

      <div className="sticky bottom-0 flex items-center gap-3 border-t border-borderDefault bg-bgWhite px-8 py-4">
        {previewCount > 0 && (
          <Button
            variant="primary"
            size="md"
            className="!bg-textDark !border-textDark !px-8"
            disabled={isConfirming}
            onClick={handleConfirmImport}
          >
            {isConfirming
              ? "Creating drafts…"
              : `Confirm Import (${previewCount} product${previewCount !== 1 ? "s" : ""})`}
          </Button>
        )}
        {draftCount > 0 && (
          <Button
            variant="primary"
            size="md"
            className="!bg-textDark !border-textDark !px-8"
            disabled={isSubmittingAll || submittingId != null}
            onClick={handleSubmitAll}
          >
            {isSubmittingAll
              ? "Submitting…"
              : `Submit All for Review (${draftCount})`}
          </Button>
        )}
        {previewCount === 0 && draftCount === 0 && confirmed.length > 0 && (
          <Button
            variant="primary"
            size="md"
            className="!bg-textDark !border-textDark !px-8"
            onClick={() => navigate(AppRoutes.MY_PRODUCT_REQUESTS)}
          >
            View All Requests
          </Button>
        )}
        <Button
          variant="secondary"
          size="md"
          className="!px-8"
          onClick={() => {
            clearBulkUploadResults();
            navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD);
          }}
        >
          {hasConfirmed ? "Upload Another File" : "Cancel"}
        </Button>
      </div>
    </div>
  );
}

export default MyProductBulkUploadResultsPage;
